import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CaretLeft as ChevronLeft, CaretRight as ChevronRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { WeekPlanCard } from "../components/WeekPlanCard";
import { ActualsCard } from "../components/ActualsCard";
import { PendingPayments } from "../components/PendingPayments";
import { RelayLoader } from "../components/RelayLoader";
import { mask, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  green: "var(--pr-green)",
  red: "var(--pr-red)",
};

const fmt = (n) => (n == null ? "n/a" : mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`));
// Pending-payment UI is gated PER-ACCOUNT via data.pending_enabled (same server-side
// allowlist that enables the sheet writers).
const shortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${d}`;
};
const fullDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const shiftDays = (iso, delta) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};

export default function EntryHistoryPage() {
  usePrivacy();  // re-render when Creator Privacy Mode flips
  const [view, setView] = useState("loading"); // loading | signed_out | not_member | ready | error
  const [data, setData] = useState(null);
  const [weekOf, setWeekOf] = useState("");
  const [editing, setEditing] = useState(null); // { date, amount }
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (dateIso) => {
    try {
      const q = dateIso ? `?week_of=${dateIso}` : "";
      const res = await fetch(`${API}/api/caddy/week-entries${q}`, { credentials: "include" });
      if (res.status === 401) { setView("signed_out"); return; }
      if (res.status === 403) { setView("not_member"); return; }
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Couldn't load that week"); return; }
      setData(json);
      setWeekOf(json.week_start);
      setView("ready");
    } catch {
      setView(data ? "ready" : "error");
    }
  }, [data]);

  useEffect(() => {
    const wk = new URLSearchParams(window.location.search).get("week_of");
    load(wk || undefined);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const save = async (amt, dateIso) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/log`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt, date: dateIso, mode: "replace" }),
      });
      const json = await res.json();
      if (!res.ok) { toast.error(typeof json.detail === "string" ? json.detail : "Couldn't save that"); return; }
      toast.success(amt === 0 ? "Entry deleted ✓" : `${fmt(amt)} saved for ${shortDate(dateIso)} ✓`);
      setEditing(null);
      setAmount("");
      await load(weekOf);
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  if (view === "error") return <CaddyErrorCard />;
  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="history-loading">
        <RelayLoader label="Pulling up your entries…" />
      </div>
    );
  }
  if (view === "signed_out" || view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: COLOR.ink2 }}>
            {view === "signed_out" ? "Sign in to see your entries." : "This is a Pit Crew page."}
          </p>
          <Link to={view === "signed_out" ? "/login" : "/app"} className="text-sm font-bold underline" style={{ color: COLOR.ink }}>
            {view === "signed_out" ? "Sign in →" : "Back to Payday Relay →"}
          </Link>
        </div>
      </div>
    );
  }

  const canGoBack = !data.earliest_logged || data.week_start > data.earliest_logged;

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="history-page">
      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="history" />
        <main className="px-5 py-6 lg:px-0">
          <div className="max-w-2xl mx-auto lg:mx-0">
            <Link to="/app" className="inline-flex items-center gap-1.5 text-sm font-semibold mb-5" style={{ color: COLOR.ink2 }} data-testid="history-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Payday Relay
            </Link>

            <h1 className="text-2xl lg:text-3xl mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Entry history
            </h1>
            <p className="text-sm leading-relaxed mb-5" style={{ color: COLOR.ink2 }}>
              Step back through any relay week to fix, add or delete what you logged.
            </p>

            {/* Week stepper */}
            <div className="flex items-center justify-between rounded-2xl px-3 py-2.5 mb-4 pr-card" data-testid="history-week-stepper">
              <button
                type="button"
                disabled={!canGoBack || busy}
                onClick={() => load(shiftDays(data.week_start, -7))}
                className="p-2 rounded-full disabled:opacity-30"
                style={{ color: COLOR.ink }}
                aria-label="Previous week"
                data-testid="history-prev-week"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: COLOR.ink }} data-testid="history-week-label">
                  {data.relay_number ? `Relay ${data.relay_number} · ` : ""}{shortDate(data.week_start)} – {shortDate(data.week_end)}
                </p>
                <p className="text-[11px]" style={{ color: COLOR.muted }} data-testid="history-week-total">
                  {fmt(data.earned)} logged{data.weekly_target ? ` of ${fmt(data.weekly_target)}` : ""}
                  {data.is_current_week ? " · this week" : ""}
                </p>
              </div>
              <button
                type="button"
                disabled={data.is_current_week || busy}
                onClick={() => load(shiftDays(data.week_start, 7))}
                className="p-2 rounded-full disabled:opacity-30"
                style={{ color: COLOR.ink }}
                aria-label="Next week"
                data-testid="history-next-week"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Inline editor */}
            {editing && (
              <div className="rounded-2xl p-4 mb-4 pr-card" data-testid="history-editor">
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>
                  {editing.amount ? "Edit" : "Add"} · {fullDate(editing.date)}
                </p>
                <div className="mt-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold" style={{ color: COLOR.ink }}>$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      autoFocus
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-3 py-2.5 rounded-xl border text-lg font-bold"
                      style={{ borderColor: COLOR.line, color: COLOR.ink, background: "var(--pr-bg)" }}
                      data-testid="history-amount-input"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      disabled={busy || amount === ""}
                      onClick={() => save(Number(amount), editing.date)}
                      className="flex-1 px-4 py-2.5 rounded-full text-sm font-bold disabled:opacity-40"
                      style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                      data-testid="history-save-btn"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditing(null); setAmount(""); }}
                      className="px-5 py-2.5 rounded-full text-sm font-bold border"
                      style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "var(--pr-card)" }}
                      data-testid="history-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <p className="text-[11px] mt-2" style={{ color: COLOR.muted }}>
                  This replaces whatever is on that day. Your sheet updates with it.
                </p>
              </div>
            )}

            <WeekPlanCard
              plan={data.week_plan}
              weeklyTarget={data.weekly_target}
              today={data.today}
              title={data.is_current_week ? "This week" : "That week"}
              showDailyTarget={data.is_current_week}
              onLogDay={(d) => {
                const day = data.week_plan?.days?.find((x) => x.date === d);
                setEditing({ date: d, amount: day?.amount || 0 });
                setAmount(day?.amount ? String(day.amount) : "");
              }}
              onDeleteDay={(d) => save(0, d)}
              onChanged={() => load(weekOf)}
            />

            {data.pending_enabled && (
              <div className="mt-5">
                <PendingPayments
                  api={API}
                  fmt={fmt}
                  color={COLOR}
                  today={data.today}
                  onSummary={() => load(weekOf)}
                />
              </div>
            )}

            {/* Close out this past relay's allocations (non-blocking, reuses the dashboard card) */}
            {!data.is_current_week && !data.closed && !data.week_off && data.weekly_target > 0 && (
              <div className="mt-5" data-testid="history-closeout">
                <ActualsCard
                  prompt={{
                    week_start: data.week_start,
                    week_end: data.week_end,
                    relay_number: data.relay_number,
                    received: data.received,
                    awaiting: data.awaiting,
                    earned: data.earned,
                    awaiting_only: (data.received ?? 0) <= 0.004 && (data.awaiting ?? 0) > 0.004,
                    ideal: data.ideal,
                  }}
                  onSaved={() => load(weekOf)}
                />
              </div>
            )}
          </div>
          <div className="h-20 lg:hidden" />
        </main>
      </div>
      <CaddyBottomNav active="more" />
    </div>
  );
}
